import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { ActivityTrackingProvider } from "./lib/activityTracking";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import ProductHubPage from "./pages/ProductHubPage";
import DashboardPage from "./pages/DashboardPage";
import CasesPage from "./pages/CasesPage";
import NewCasePage from "./pages/NewCasePage";
import CaseDetailPage from "./pages/CaseDetailPage";
import EditCasePage from "./pages/EditCasePage";
import SearchPage from "./pages/SearchPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import SubscribePage from "./pages/SubscribePage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import SecuritySettingsPage from "./pages/SecuritySettingsPage";
import AiNoticeRequiredPage from "./pages/AiNoticeRequiredPage";

// Intake.AI Pages
import IntakeAppPage from "./pages/IntakeAppPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import AgreementPage from "./pages/AgreementPage";
import SignAgreementPage from "./pages/SignAgreementPage";
import ClientUploadPage from "./pages/ClientUploadPage";
import ClientIntakePage from "./pages/ClientIntakePage";
import IntakeSettingsPage from "./pages/IntakeSettingsPage";
import IntakeFormCustomizationPage from "./pages/IntakeFormCustomizationPage";
import PublicIntakePage from "./pages/PublicIntakePage";

// Workflow.AI Pages
import SharedWorkflowPage from "./pages/SharedWorkflowPage";

// Case Management.AI Pages
import CaseManagementPage from "./pages/CaseManagementPage";
import CaseManagementDetailPage from "./pages/CaseManagementDetailPage";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";

// Protected Route component - requires login, active subscription, AND AI notice acknowledgment
function ProtectedRoute({ children }) {
  const { user, loading, isSubscribed, hasAcknowledgedAiNotice } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-12 h-12 rounded-md bg-navy/20 mx-auto mb-4" />
          <p className="text-muted-foreground font-body">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check subscription status - redirect to subscribe page if not active
  if (!isSubscribed()) {
    return <Navigate to="/subscribe" replace />;
  }

  // Check AI notice acknowledgment - redirect to acknowledgment page if not acknowledged
  if (!hasAcknowledgedAiNotice()) {
    return <Navigate to="/acknowledge-ai-notice" replace />;
  }

  return children;
}

// Auth Route - requires login but not subscription (for payment pages)
function AuthRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-12 h-12 rounded-md bg-navy/20 mx-auto mb-4" />
          <p className="text-muted-foreground font-body">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route component (redirect to product hub if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <div className="w-12 h-12 rounded-md bg-navy/20 mx-auto mb-4" />
          <p className="text-muted-foreground font-body">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/hub" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing page - public marketing homepage */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      
      {/* Product Hub - main dashboard after login */}
      <Route
        path="/hub"
        element={
          <AuthRoute>
            <ProductHubPage />
          </AuthRoute>
        }
      />

      {/* Legacy routes - redirect to new paths */}
      <Route path="/exhibits/login" element={<Navigate to="/login" replace />} />
      <Route path="/exhibits/register" element={<Navigate to="/register" replace />} />
      <Route path="/exhibits/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/exhibits/cases" element={<Navigate to="/cases" replace />} />
      <Route path="/exhibits/cases/new" element={<Navigate to="/cases/new" replace />} />
      <Route path="/exhibits/search" element={<Navigate to="/search" replace />} />
      
      <Route
        path="/terms-of-service"
        element={<TermsOfServicePage />}
      />

      {/* AI Notice Acknowledgment - required before accessing protected routes */}
      <Route
        path="/acknowledge-ai-notice"
        element={
          <AuthRoute>
            <AiNoticeRequiredPage />
          </AuthRoute>
        }
      />

      {/* Payment routes - require auth but not subscription */}
      <Route
        path="/payment/success"
        element={
          <AuthRoute>
            <PaymentSuccessPage />
          </AuthRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <AuthRoute>
            <PaymentCancelPage />
          </AuthRoute>
        }
      />
      <Route
        path="/subscribe"
        element={
          <AuthRoute>
            <SubscribePage />
          </AuthRoute>
        }
      />

      {/* Protected routes - require auth AND subscription */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <CasesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/new"
        element={
          <ProtectedRoute>
            <NewCasePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:id"
        element={
          <ProtectedRoute>
            <CaseDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:id/edit"
        element={
          <ProtectedRoute>
            <EditCasePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <SearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security"
        element={
          <ProtectedRoute>
            <SecuritySettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Intake.AI Routes */}
      <Route
        path="/intake"
        element={
          <ProtectedRoute>
            <IntakeAppPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake/leads/:leadId"
        element={
          <ProtectedRoute>
            <LeadDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake/leads/:leadId/agreement"
        element={
          <ProtectedRoute>
            <AgreementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake/settings"
        element={
          <ProtectedRoute>
            <IntakeSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake/customize"
        element={
          <ProtectedRoute>
            <IntakeFormCustomizationPage />
          </ProtectedRoute>
        }
      />
      
      {/* Workflow.AI Routes - redirect to case management */}
      <Route
        path="/workflow"
        element={<Navigate to="/case-management" replace />}
      />
      
      {/* Billing.AI Routes - redirect to case management */}
      <Route
        path="/billing"
        element={<Navigate to="/case-management?tab=billing-ai" replace />}
      />
      
      {/* Examination.AI Routes - redirect to case management */}
      <Route
        path="/examination"
        element={<Navigate to="/case-management" replace />}
      />
      
      {/* Case Management.AI Routes */}
      <Route
        path="/case-management"
        element={
          <ProtectedRoute>
            <CaseManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/case-management/:caseId"
        element={
          <ProtectedRoute>
            <CaseManagementDetailPage />
          </ProtectedRoute>
        }
      />
      
      {/* Public routes - no auth required */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/sign-agreement/:agreementId" element={<SignAgreementPage />} />
      <Route path="/client-upload/:token" element={<ClientUploadPage />} />
      <Route path="/client-intake/:token" element={<ClientIntakePage />} />
      <Route path="/intake/:slug" element={<PublicIntakePage />} />
      <Route path="/workflow/shared/:token" element={<SharedWorkflowPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ActivityTrackingProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </ActivityTrackingProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
